import { pluck, isFunction } from "underscore";
import analytics from "web/script/analytics/analytics";
import baustein from "baustein";
import facebookEvents from "web/script/analytics/facebook-events";
import doubleclickEvents from "web/script/analytics/doubleclick-events";
import environment from "web/script/modules/environment";
import globals from "web/script/modules/globals";
import requester from "web/script/modules/requester";
import browser from "web/script/utils/browser";
import logging from "web/script/utils/logging";
import { ACCORDION_TITLE_CLASS } from "web/script/components/accordions";


const name = "buybuybuy-area";

export const selectors = {
    SELECT_SIZE_CTA: `.${name}__size-options__select-size-button`,
    SIZE_SELECT: `.${name}__size-options__select-size-input`,
    SIZE_SELECT_WRAPPER: `.${name}__size-options__select-wrapper`,
    STORES_COUNT_TEXT: `.${name}__size-options__stores-count-text`,
    ERROR_AREA: `.${name}__error-area`,
    SIZE_GUIDE: `.${name}__size-guide-link`,
    APP_HEADER: ".app-header",
    BUY_OPTION_BUY_BUTTON: ".buybuybuy-option__buy-button",
    AFFILIATE_OPTION: ".buybuybuy-affiliate-option",
    ACCORDION: ".accordion",
    UX_SCORE: ".ux-score",
};

const states = {
    CTA_ERROR: `${name}__cta--error`,
    SIZE_SELECT_ERROR: `${name}__size-options__select-wrapper--error`,
    ACCORDION_EXPANDED: "accordion--expanded",
};

const ADD_TO_CART_ENDPOINT = "/cart/add/";

let optionsPerSizeNum = 0;
let affiliateOptionsNum = 0;

/**
 * NEW BUY AREA IN NEW PDP
 *
 * This is a new iteration of our PDP.
 * Being a test, is not all the refined it could be.
 *
 * @TODO This component has been reverse engineered due to new PDP performing poorly, needs to
 *       be refactored.
 *
 * @see https://confluence.lystit.com/display/WEBCETERA/BuyBuyBuy+PDP
 *
 * The main reponsabilities of the component are:
 * - handle size selection
 * - triggering buy options display
 * - handling add-to-bag behaviour.
 *
 * @borrows _addToBag, _sendBuyActionAnalytics, _addToCart, _showErrorMessage, _disableBuyButtons,
 *          _enableBuyButtons and _getBuyButtons from components/monoprice-buy-area
 *          @todo refactor into a mixin? both components consume the same actions...
 *
 * @see components/buybuybuy-options
 * @see components/buybuybuy-option
 * @see components/monoprice-buy-area
 */
export default baustein.register(name, {
    defaultOptions: {
        productId: null,
        isSavedForLater: false,
    },

    setupEvents(add) {
        add("click", selectors.SELECT_SIZE_CTA, this._onSelectSizeCTA);
        add("focus", selectors.SIZE_SELECT, this._handleSizeFocus);
        add("change", selectors.SIZE_SELECT, this._triggerBuyOptions);
        add("buyOptionsLoaded", this._handleOptionsLoaded);
        add("buyOptionsReady", this._computeVisibleBuyOptions);
        add("buyOptionAddToBag", this._addToBag);
    },

    onInsert() {
        const buyOptions = this.findComponent("buybuybuy-options");

        if (buyOptions.options.withSearch) {
            const sizeCta = this.el.querySelector(selectors.SELECT_SIZE_CTA);
            const uxScore = document.querySelector(selectors.UX_SCORE);
            this._registerStickyBuyButton([sizeCta, uxScore]);
        }

        this._countAffiliateBuyOptions();
        this._setInitialState();
    },

    /**
     * The HTML as-rendered is designed to work without JS, this function overrides that
     * state to create an initial state which is richer for the user, and takes advantage
     * of javascript to create a better experience.
     */
    _setInitialState() {
        const triggerEl = this.el.querySelector(selectors.SELECT_SIZE_CTA);
        const storesCountEl = this.el.querySelector(selectors.STORES_COUNT_TEXT);
        const buyOptions = this.findComponent("buybuybuy-options");

        if (!buyOptions.options.withSearch) {
            triggerEl.classList.add("hidden");
            storesCountEl.classList.remove("hidden");
        }

        // override the "Size - Price" in each dropdown item in the js-less version of the page to just Size
        [...this.el.querySelector(selectors.SIZE_SELECT).options].forEach((sizeOptionEl) => {
            let humanSize = sizeOptionEl.dataset["humanSize"];
            if (humanSize) {
                sizeOptionEl.innerHTML = humanSize;
            }
        });
    },

    /**
     * Triggers buy options display which consists of a small search animation followed by the
     * actual display of the buy options. On narrower viewports, it will try to center the viewport
     * relative to the buy options so the user is aware of what's goingo on.
     *
     * If the Buy Options are loaded, it'll update them without re-triggering the whole
     * search animation. `_computeVisibleBuyOptions` is a key part for the async transitions between
     * animations to succeed.
     *
     * @see _computeVisibleBuyOptions
     * @returns {undefined}
     */
    _triggerBuyOptions() {
        const triggerEl = this.el.querySelector(selectors.SELECT_SIZE_CTA);
        const buyOptions = this.findComponent("buybuybuy-options");

        this._handleSize(triggerEl);

        if (buyOptions.loaded) {
            return buyOptions.hideResults().then(() => {
                this._computeVisibleBuyOptions();
                this._cleanErrors();
                buyOptions.showResults(Math.random() * 500);
            });
        }

        requestAnimationFrame(() => {
            this._cleanErrors();
            triggerEl.setAttribute("disabled", true);

            if (buyOptions) {
                if (browser.getViewport() === browser.VIEWPORTS.DESKTOP) {
                    buyOptions.start(300);
                } else {
                    this._centerOnViewport(
                        this.el.querySelector(selectors.SIZE_GUIDE),
                        buyOptions.start.bind(buyOptions, 300)
                    );
                }
            }
        });
    },

    /**
     * Queries all the available Buy Options and hides the ones not related to the current size.
     * Assumes that the Buy Options came sorted by cheapest option from the server and adds the
     * 'expanded' state to the first matching the selected size.
     *
     * @returns {undefined}
     */
    _computeVisibleBuyOptions() {
        const accordions = this.findComponent("accordions");
        const accordionEls = accordions.el.querySelectorAll(selectors.ACCORDION);
        const size = this._getSelectedSize();
        let first = true;

        optionsPerSizeNum = 0;

        [...accordionEls].forEach((acc) => {
            let optionType = acc.getAttribute("data-option-type");
            let condition =
                optionType === "affiliate" || acc.getAttribute("data-raw-size") === size;
            let titleEl = acc.querySelector(`.${ACCORDION_TITLE_CLASS}`);

            condition ? acc.classList.remove("hidden") : acc.classList.add("hidden");

            if (condition) {
                accordions.toggleTarget(titleEl, first);
                first = false;
                const buyButton = acc.querySelector(selectors.BUY_OPTION_BUY_BUTTON);
                const uxScore = document.querySelector(selectors.UX_SCORE);
                this._registerStickyBuyButton([buyButton, uxScore]);

                if (optionType !== "affiliate") {
                    ++optionsPerSizeNum;
                }
            }
        });
    },

    /**
     * Gets the current selected size value. If there' no current selected size, it defaults to
     * the first option. Consumed by _computeVisibleBuyOptions.
     *
     * @see _computeVisibleBuyOptions
     * @returns {undefined}
     */
    _getSelectedSize() {
        const sizeEl = this.el.querySelector(selectors.SIZE_SELECT);
        let selectedSize = sizeEl.options[sizeEl.selectedIndex].dataset["rawSize"];
        return selectedSize ? selectedSize : sizeEl.options[1].dataset["rawSize"];
    },

    /**
     * Sends analytics event upon focus even for size picker
     *
     * @param {Object} event Baustein's wrapper around native DOM event object.
     * @returns {undefined}
     */
    _handleSizeFocus(event) {
        // Get number of size options after filtering out null ones
        const optionsLength = Array.from(event.target.options).filter((option) => option.value)
            .length;

        analytics.event("buy_area", "size_picker_focus", optionsLength);
    },

    /**
     * Small wrapper to leave _handleSize event agnostic.
     *
     * @param {Object} event Baustein's wrapper around native DOM event object.
     * @param {HTMLElement} target The DOM element which triggered the event.
     * @returns {undefined}
     */
    _onSelectSizeCTA(event, target) {
        this._handleSize(target);
    },

    /**
     * Handles whether there user has selected a size or not and updates the state of the main
     * Buy Area CTA accordingly.
     *
     * @returns {undefined}
     */
    _handleSize(target) {
        const targetTextEl = target.querySelector(".text-paragraph");
        const targetGlyphEl = target.querySelector(".glyph");
        const selectSizeWrapperEl = this.el.querySelector(selectors.SIZE_SELECT_WRAPPER);

        if (this._isSizeSelected()) {
            this._removeSizePickerPlaceholder();
        } else {
            target.classList.add(states.CTA_ERROR);
            selectSizeWrapperEl.classList.add(states.SIZE_SELECT_ERROR);
            targetGlyphEl.classList.add("hidden");
            targetTextEl.textContent = "Please pick a size";
            analytics.event("buy_area", "pick_a_size_error");
        }
    },

    /**
     * Asserts whether a size is currently selected on the size picker or not.
     * Consumed by handleSize().
     *
     * @see handleSize()
     * @returns {boolean}
     */
    _isSizeSelected() {
        const selectSizeEl = this.el.querySelector(selectors.SIZE_SELECT);
        const size = selectSizeEl.value;

        return size !== "";
    },

    /**
     * Removes the first option on the size picker (usually a placeholder option acting as
     * a label for the size picker).
     * Consumed by handleSize().
     *
     * @see handleSize()
     * @returns {undefined}
     */
    _removeSizePickerPlaceholder() {
        const selectSizeEl = this.el.querySelector(selectors.SIZE_SELECT);

        if (selectSizeEl.firstElementChild.value === "") {
            selectSizeEl.removeChild(selectSizeEl.firstElementChild);
        }
    },

    /**
     * If the Sticky Buy Button is present, make it hide when the supplied target is visible
     * within the viewport and not overlapped by the page Header.
     *
     * @see commponents/buybuybuy-sticky-buy-button
     * @param {Array<HTMLElement>} targets The HTMLElements to take as a references for when to hide the
     *                                     Sticky Buy Button.
     * @returns {undefined}
     */
    _registerStickyBuyButton(targets) {
        const stickyBuyButton = this.findComponent("buybuybuy-sticky-buy-button");

        if (stickyBuyButton) {
            stickyBuyButton.hideWhenIsVisible(targets);
        }
    },

    /**
     * Removes error states for all the CTAs in the Buy Area.
     * Useful if you want to make sure that all the CTAs in the Buy Options have no error states.
     * Consumed by _triggerBuyOptions to make sure there's no error states on the different
     * 'Add to Bag' CTAs when displaying the Buy Options once the user changes size via the
     * size picker.
     *
     * @see _triggerBuyOptions
     * @returns {undefined}
     */
    _cleanErrors() {
        const errorCTAels = this.el.querySelectorAll(`.${states.CTA_ERROR}`);
        const selectSizeWrapperEl = this.el.querySelector(selectors.SIZE_SELECT_WRAPPER);

        selectSizeWrapperEl.classList.remove(states.SIZE_SELECT_ERROR);

        [...errorCTAels].forEach((cta) => {
            const glyphEl = cta.querySelector(".glyph");
            const ctaTextEl = cta.querySelector(".text-paragraph");

            glyphEl.classList.remove("hidden");
            ctaTextEl.textContent = "Add to bag";

            cta.classList.remove(states.CTA_ERROR);
        });
    },

    /**
     * Displays some text explaining how many stores were found selling the product.
     * Called once the Search animtion is done when we have multiple retailers.
     *
     * @returns {undefined}
     */
    _handleOptionsLoaded() {
        const triggerEl = this.el.querySelector(selectors.SELECT_SIZE_CTA);
        const storesCountTextEl = this.el.querySelector(selectors.STORES_COUNT_TEXT);
        const count = optionsPerSizeNum + affiliateOptionsNum;

        requestAnimationFrame(() => {
            storesCountTextEl.textContent = `${count} store${
                count > 1 ? "s" : ""
            } selling this size`;
            triggerEl.classList.add("hidden");
            storesCountTextEl.classList.remove("hidden");
        });
    },

    /**
     * If we have Affiliate Buy Options, cache how many of them we have.
     * Used for outputting a correct stores count on _handleOptionsLoaded .
     *
     * @see onInsert
     * @see _handleOptionsLoaded
     * @returns {undefined}
     */
    _countAffiliateBuyOptions() {
        const affiliateOptionsEls = this.el.querySelectorAll(selectors.AFFILIATE_OPTION);

        if (affiliateOptionsEls.length) {
            affiliateOptionsNum = affiliateOptionsEls.length;
        }
    },

    /**
     * Tries to vertically focus the viewport around the supplied element (with 300ms of easing),
     * and calls an optional supplied callback when done.
     *
     * @param {HTMLElement} el The element to scroll the viewport to.
     * @param {Function} cb A function to call once the centering action is complete.
     * @return {undefined}
     */
    _centerOnViewport(el, cb) {
        if (!el) {
            return;
        }
        const offset = el.getBoundingClientRect().top;
        const easing = 300;
        const header = document.querySelector(selectors.APP_HEADER);
        const headerOffset = header.getBoundingClientRect().height;

        browser
            .scrollTop(
                // 10 is a magic number. In order to calculate the exact position programatically, we
                // would need to query the DOM too many items, which would make this function inneficient
                // (it runs during critical re-paints). We can assume that, the space occupied by elements
                // on top of the size picker on narrow viewports is always the same.
                offset + window.pageYOffset - (headerOffset + 10),
                easing
            )
            .then(() => {
                if (isFunction(cb)) {
                    cb();
                }
            });
    },

    /**
     * Triggers the action of adding an item to the cart and logs any errors related to analytics.
     * If there's no currently selected size and the user tries to Add to Bag it'll add an error
     * state to the corresponding CTA.
     *
     * @see components/buybuybuy-option _onBuyAction
     * @param {Object} event The supplied event object from Baustein's custom event.
     * @return {undefined}
     */
    _addToBag(event) {
        const { options, target } = event;
        const { rawSize, linkId } = options;

        if (!this._isSizeSelected()) {
            this._handleSize(target);
            return;
        }

        this._sendBuyActionAnalytics(options).catch((err) => {
            console.error("Analytics on buy action failed.");
            logging.error(err);
        });

        this._addToCart(rawSize, linkId);
    },

    /**
     * Sends all the analytics data we need when a user clicks on the buy button.
     *
     * @param {Object} options Supplied options for the current component.
     * @returns {Promise} A list of promises relating to analytics actions.
     */
    _sendBuyActionAnalytics(options) {
        const label = this.options.isSavedForLater
            ? "save_for_later_add_to_cart"
            : "add_to_cart_button";

        const analyticsPromises = [
            analytics.ecommerce("ec:addProduct", {
                name: environment.get("analyticsProduct.product_canonical_slug"),
                category: environment.get("analyticsProduct.ga_category"),
                brand: environment.get("analyticsProduct.designer_slug"),
                variant: environment.get("analyticsProduct.product_uid"),
                price: options.price,
                quantity: 1,
                dimension20: environment.get("analyticsProduct.gender"),
                dimension29: environment.get("analyticsProduct.color"),
                dimension28: true,
                dimension30: false,
                dimension32: "Lyst",
                dimension34: options.rawSize,
                dimension23: options.retailerSlug,
                metric8: options.discount,
                dimension31: options.onFinalSale,
                dimension27: options.hasFreeShipping,
            }),
            analytics.ecommerce("ec:setAction", "add"),
            analytics.event("CART", "ADD_ITEM", label),
            analytics.tagEvent(
                analytics.TAG_NETWORKS.FACEBOOK,
                facebookEvents.ADD_TO_CART,
                {
                    content_ids: [this.options.productId],
                    content_type: "product",
                },
                true
            ),
            analytics.tagEvent(analytics.TAG_NETWORKS.DOUBLECLICK, doubleclickEvents.ADD_TO_CART, {
                path: globals.window.location.pathname,
                size: options.rawSize,
            }),
        ];
        return Promise.all(analyticsPromises);
    },

    /**
     * Supplies the selected size and linkId for the current product to the cart API.
     * Handles any errors coming from cart_add API.
     *
     * @param {number} rawSize The raw size associated to the current product.
     * @param {number} linkId The linkId associated to the current product.
     * @returns {undefined}
     */
    _addToCart(rawSize, linkId) {
        const requestObj = {
            icon: true,
            raw_size: rawSize,
            link_id: linkId,
        };

        const requestData = {
            body: {
                buy_option: JSON.stringify(requestObj),
                is_base64_encoded: false,
            },
        };

        this._disableBuyButtons();

        return requester(ADD_TO_CART_ENDPOINT, requestData)
            .then(requester.toJSON)
            .then((response) => {
                if (!response.success) {
                    let error = new Error();
                    error.response = response;
                    throw error;
                }

                this.emit("addedToBag");
                this._enableBuyButtons();
                return true;
            })
            .catch((err) => {
                logging.error(err.response);
                analytics.event("CART", "ADD_TO_BAG_ERROR");

                const errorMessage = pluck(err.response["data"], "message").join(" ");

                if (
                    errorMessage.indexOf(
                        "something went wrong trying to add this product to your cart"
                    ) > -1
                ) {
                    analytics.event("cart", "add_to_cart_error", "cart_add_item_error");
                } else if (
                    errorMessage.indexOf("This product seems to no longer be in stock") > -1
                ) {
                    analytics.event("cart", "add_to_cart_error", "product_no_longer_in_stock");
                } else if (errorMessage.indexOf("You need to select an option") > -1) {
                    analytics.event("cart", "add_to_cart_error", "no_buy_option_detected");
                } else if (errorMessage.indexOf("You need to select a size") > -1) {
                    analytics.event("cart", "add_to_cart_error", "no_size_detected");
                } else if (errorMessage.indexOf("you cannot have more than") > -1) {
                    analytics.event("cart", "add_to_cart_error", "too_many_items_in_cart");
                }

                this._showErrorMessage(errorMessage);
                this._enableBuyButtons();
                return err;
            });
    },

    /**
     * Shows an error message to the user, usually relating to a failed request to cart_add API.
     *
     * @param {string} message The message to display to the user.
     * @returns {undefined}
     */
    _showErrorMessage(message) {
        const errorArea = this.el.querySelector(selectors.ERROR_AREA);
        errorArea.textContent = message;
        errorArea.classList.remove("hidden");
    },

    /**
     * Disables the add-to-cart CTAs so the user can't interact with them while any request to
     * the requeest to cart_add API is processing.
     *
     * @returns {undefined}
     */
    _disableBuyButtons() {
        const buyButtons = this._getBuyButtons();
        buyButtons.forEach((button) => {
            button.disabled = true;
        });
    },

    /**
     * Enables the add-to-cart CTAs so the user can interact with them again.
     *
     * @returns {undefined}
     */
    _enableBuyButtons() {
        const buyButtons = this._getBuyButtons();
        buyButtons.forEach((button) => {
            button.disabled = false;
        });
    },

    /**
     * Wrapper around `querySelectorAll` for getting all the add-to-cart CTAs within the component.
     *
     * @returns {undefined}
     */
    _getBuyButtons() {
        return Array.from(this.el.querySelectorAll(selectors.BUY_OPTION_BUY_BUTTON));
    },
});
